import { gql, OperationVariables, TypedDocumentNode } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ITenant } from '../interfaces/Tenant';
import { Query } from '../services/graphql/query';
import { getContextForRequest } from '../shared/ApolloHooksUtils';

const emptyTenants: ITenant[] = [];
type Snapshot = { userId: number; account: unknown; tenants: ITenant[] };
type ReportError = (options: { context: string; error: unknown }) => void;
type AssociatedTenantsResponse = { tenantsAssociatedWithUser: ITenant[] };

const associatedTenantsQuery = gql(
  Query.tenantsAssociatedWithUser.gql,
) as TypedDocumentNode<AssociatedTenantsResponse, OperationVariables>;

export function useAssociatedTenants(
  userId: number | undefined,
  readAccount: () => unknown,
  reportError: ReportError,
  enabled = true,
) {
  const client = useApolloClient();
  const account = readAccount();
  const latest = useRef({ userId, account, readAccount, reportError, enabled });
  latest.current = { userId, account, readAccount, reportError, enabled };
  const [snapshot, setSnapshot] = useState<Snapshot>();
  const sequence = useRef(0);
  const controller = useRef<AbortController | undefined>(undefined);
  const active = useRef(false);

  const getTenantsAssociatedWithUser = useCallback(async (): Promise<
    ITenant[]
  > => {
    if (
      !enabled ||
      !latest.current.enabled ||
      !userId ||
      !active.current ||
      latest.current.userId !== userId ||
      account !== latest.current.readAccount()
    )
      return emptyTenants;
    const requestId = ++sequence.current;
    controller.current?.abort();
    const requestController = new AbortController();
    controller.current = requestController;
    const isCurrent = () =>
      active.current &&
      latest.current.enabled &&
      requestId === sequence.current &&
      latest.current.userId === userId &&
      account === latest.current.readAccount();
    try {
      const { data, error } = await client.query({
        query: associatedTenantsQuery,
        fetchPolicy: 'no-cache',
        errorPolicy: 'none',
        context: {
          ...getContextForRequest(Query.tenantsAssociatedWithUser),
          queryDeduplication: false,
          fetchOptions: { signal: requestController.signal },
        },
      });
      if (!isCurrent()) return emptyTenants;
      if (error) throw error;
      const tenants = data?.tenantsAssociatedWithUser ?? emptyTenants;
      setSnapshot({ userId, account, tenants });
      return tenants;
    } catch (error) {
      if (isCurrent())
        latest.current.reportError({
          context: 'App.tenantsAssociatedWithUser',
          error,
        });
      return emptyTenants;
    }
  }, [client, userId, account, enabled]);

  useEffect(() => {
    active.current = true;
    let currentSetup = true;
    if (userId && enabled) {
      // StrictMode cleans up its first setup before this microtask can start a request.
      void Promise.resolve().then(() => {
        if (currentSetup) void getTenantsAssociatedWithUser();
      });
    } else setSnapshot(undefined);
    return () => {
      currentSetup = false;
      active.current = false;
      sequence.current++;
      controller.current?.abort();
    };
  }, [userId, enabled, getTenantsAssociatedWithUser]);

  const setTenantsAssociatedToUser: Dispatch<SetStateAction<ITenant[]>> =
    useCallback(
      (action) => {
        if (!userId) return;
        setSnapshot((previous) => {
          const currentTenants =
            previous?.userId === userId && previous.account === account
              ? previous.tenants
              : emptyTenants;
          const tenants =
            typeof action === 'function' ? action(currentTenants) : action;

          return { userId, account, tenants };
        });
      },
      [userId, account],
    );

  return {
    tenantsAssociatedToUser:
      enabled &&
      snapshot &&
      snapshot.userId === userId &&
      snapshot.account === account
        ? snapshot.tenants
        : emptyTenants,
    setTenantsAssociatedToUser,
    getTenantsAssociatedWithUser,
  };
}
